import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const usersAdapter = createEntityAdapter({})
const initialState = usersAdapter.getInitialState()

export const usersApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUsers: builder.query({
            query: () => ({
                url: '/users',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedUsers = responseData.map(user => {
                    user.id = user._id
                    return user
                });
                return usersAdapter.setAll(initialState, loadedUsers)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'User', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'User', id }))
                    ]
                } else return [{ type: 'User', id: 'LIST' }]
            }
        }),
        addNewUser: builder.mutation({
            query: initialUserData => ({
                url: '/users',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        approveNewUser: builder.mutation({
            query: initialUserData => ({
                url: '/users/approve',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        addBulkUser: builder.mutation({
            query: initialUserData => ({
                url: '/users/addbulk',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        updateBulkUsers: builder.mutation({
            query: userData => ({
                url: '/users/updatebulk',
                method: 'PUT',
                body: {
                    ...userData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        updateUser: builder.mutation({
            query: initialUserData => ({
                url: '/users',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),
        deleteUser: builder.mutation({
            query: ({ id }) => ({
                url: `/users`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),

        // when adding assets in user Doc
        addUserAssets: builder.mutation({
            query: initialUserData => ({
                url: '/users/addassets',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),

         // when adding individual assets in user Doc
         addUserIndiAssets: builder.mutation({
            query: initialUserData => ({
                url: '/users/addindiassets',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        //Bulk Update of Uuer Assets Generalized Nested Docs.
        bulkUpdateGeneralUser: builder.mutation({
            query: initialUserData => ({
                url: '/users/updatebulkgeneral',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        //Modify Array in User Documents.
        modifyArrayUser: builder.mutation({
            query: initialUserData => ({
                url: '/users/modifyarray',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),

        // when handing over assets by user
        handoverUserAssets: builder.mutation({
            query: initialUserData => ({
                url: '/users/handover',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),

        // when handing over INDIVIDUAL assets by user
        handoverIndUserAssets: builder.mutation({
            query: initialUserData => ({
                url: '/users/indhandover',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),

        // officeTrans_assets transfer (true/false)(for bo and storemanagers's assets transfer)
        handoverOffTranAssets: builder.mutation({
            query: initialUserData => ({
                url: '/users/offtransassets',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),

    }),
})

export const {
    useGetUsersQuery,
    useAddNewUserMutation,
    useApproveNewUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useAddBulkUserMutation,
    useUpdateBulkUsersMutation,
    useAddUserAssetsMutation,
    useHandoverUserAssetsMutation,
    useBulkUpdateGeneralUserMutation,
    useModifyArrayUserMutation,
    useHandoverIndUserAssetsMutation,
    useHandoverOffTranAssetsMutation,
    useAddUserIndiAssetsMutation
} = usersApiSlice

// returns the query result object
export const selectUsersResult = usersApiSlice.endpoints.getUsers.select()

// creates memoized selector
const selectUsersData = createSelector(
    selectUsersResult,
    usersResult => usersResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllUsers,
    selectById: selectUserById,
    selectIds: selectUserIds
    // Pass in a selector that returns the users slice of state
} = usersAdapter.getSelectors(state => selectUsersData(state) ?? initialState)